import { useSnackbar } from 'notistack';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { io } from 'socket.io-client';

const SocketContext = createContext(null);

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const enqueueSnackbarRef = useRef(enqueueSnackbar);
  const socketRef = useRef(null);
  const authFailedRef = useRef(false); // Bloquer les reconnexions après échec auth

  // Mettre à jour la ref pour éviter les re-renders inutiles
  useEffect(() => {
    enqueueSnackbarRef.current = enqueueSnackbar;
  }, [enqueueSnackbar]);

  // Fonction pour créer et connecter le socket
  const connectSocket = useCallback(() => {
    const token = localStorage.getItem('adminToken');

    if (!token) {
      console.log('🔌 No token, skipping socket connection');
      return null;
    }

    // Bloquer si authentification a échoué (token invalide/expiré)
    if (authFailedRef.current) {
      return null;
    }

    // Éviter les connexions multiples
    if (socketRef.current?.connected) {
      return socketRef.current;
    }

    // URL du backend pour Socket.IO (sans le path /api/v1)
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000';
    const SOCKET_URL = apiUrl.replace(/\/api\/v\d+$/, '');

    console.log('🔌 Socket.IO connecting to:', SOCKET_URL);

    const newSocket = io(SOCKET_URL, {
      auth: {
        token: `Bearer ${token}`
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });

    // Événements de connexion
    newSocket.on('connect', () => {
      console.log('✅ Socket connected');
      setIsConnected(true);
    });

    newSocket.on('disconnect', () => {
      console.log('❌ Socket disconnected');
      setIsConnected(false);
    });

    newSocket.on('connect_error', err => {
      console.error('⚠️ Socket connection error:', err.message);
      // Si erreur d'authentification, déconnecter l'utilisateur (token expiré/invalide)
      if (err.message.includes('Authentication')) {
        console.log('🔒 Token expiré ou invalide, déconnexion...');
        authFailedRef.current = true;
        newSocket.disconnect();
        // Nettoyer le token et rediriger vers login
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminData');
        window.location.href = '/login';
      }
      // Si trop de tentatives, juste arrêter
      if (err.message.includes('Too many')) {
        authFailedRef.current = true;
        newSocket.disconnect();
      }
    });

    // Attacher tous les listeners métier
    attachBusinessListeners(newSocket, enqueueSnackbarRef);

    socketRef.current = newSocket;
    return newSocket;
  }, []);

  // Effet principal pour la connexion
  useEffect(() => {
    const newSocket = connectSocket();
    if (newSocket) {
      setSocket(newSocket);

      // Vérifier si déjà connecté (cas rare mais possible)
      if (newSocket.connected) {
        setIsConnected(true);
      }
    }

    // Cleanup
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [connectSocket]);

  // Vérification périodique pour détecter les changements de token (après login)
  useEffect(() => {
    const checkTokenInterval = setInterval(() => {
      const token = localStorage.getItem('adminToken');
      const hasToken = !!token;
      const hasSocket = !!socketRef.current?.connected;

      // Si token ajouté mais pas de socket connecté, reconnecter (sauf si auth failed)
      if (hasToken && !hasSocket && !authFailedRef.current) {
        console.log('🔄 Token detected, attempting socket connection...');
        const newSocket = connectSocket();
        if (newSocket) {
          setSocket(newSocket);
        }
      }

      // Si token supprimé mais socket existe, déconnecter
      if (!hasToken && socketRef.current) {
        console.log('🔄 Token removed, disconnecting socket...');
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
        authFailedRef.current = false; // Reset pour permettre reconnexion après nouveau login
      }
    }, 1000); // Vérifier toutes les secondes

    return () => clearInterval(checkTokenInterval);
  }, [connectSocket]);

  return (
    <SocketContext.Provider value={{ socket, isConnected }}>
      {children}
    </SocketContext.Provider>
  );
};

/**
 * Attache tous les listeners d'événements métier au socket
 */
function attachBusinessListeners(socket, enqueueSnackbarRef) {
  const enqueue = (...args) => enqueueSnackbarRef.current(...args);

  // 🚨 Alertes système
  socket.on('system_alert', data => {
    enqueue(`🚨 ALERTE SYSTÈME: ${data.message}`, {
      variant: 'error',
      persist: true
    });
  });

  // 📝 Nouveaux signalements
  socket.on('new_report', data => {
    enqueue(`📝 Nouveau signalement: ${data.reason}`, {
      variant: 'warning',
      action: () => (
        <button
          onClick={() => (window.location.href = `/reports/${data.reportId}`)}
        >
          Voir
        </button>
      )
    });
  });

  // 👤 Nouveaux utilisateurs
  socket.on('new_user', data => {
    enqueue(`👤 Nouvel utilisateur: ${data.name}`, {
      variant: 'info'
    });
  });

  // 🎫 Nouveaux tickets support
  socket.on('new_ticket', data => {
    const isUrgent = data.priority === 'high' || data.priority === 'urgent';
    const prefix = isUrgent ? '🔥 SUPPORT PRIORITAIRE' : '🎫 Nouveau ticket';

    enqueue(`${prefix}: ${data.subject} (#${data.ticketNumber})`, {
      variant: isUrgent ? 'error' : 'info',
      autoHideDuration: isUrgent ? 10000 : 5000,
      action: () => (
        <button
          style={{
            color: 'white',
            fontWeight: 'bold',
            background: 'transparent',
            border: '1px solid white',
            padding: '4px 8px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
          onClick={() => (window.location.href = `/tickets/${data.ticketId}`)}
        >
          Voir
        </button>
      )
    });
  });

  // 🚨 Modération - Nouvelle violation
  socket.on('moderation:new_violation', data => {
    const isCritical = data.score >= 90;
    const contentType = data.contentType === 'message' ? 'Message' : 'Bio';

    enqueue(
      `🚨 ${isCritical ? 'VIOLATION CRITIQUE' : 'Violation détectée'}: ${contentType} de ${data.userName || 'Utilisateur'} (Score: ${data.score})`,
      {
        variant: isCritical ? 'error' : 'warning',
        autoHideDuration: isCritical ? null : 8000,
        action: () => (
          <button
            style={{
              color: 'white',
              fontWeight: 'bold',
              background: 'transparent',
              border: '1px solid white',
              padding: '4px 8px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            onClick={() =>
              (window.location.href = `/moderation/violations/${data.violationId}`)
            }
          >
            Examiner
          </button>
        )
      }
    );
  });

  // 📋 Nouvelle demande de vérification
  socket.on('new_verification', data => {
    enqueue(
      `📋 Nouvelle vérification: ${data.userName} (tentative #${data.attemptNumber})`,
      {
        variant: 'info',
        autoHideDuration: 8000,
        action: () => (
          <button
            style={{
              color: 'white',
              fontWeight: 'bold',
              background: 'transparent',
              border: '1px solid white',
              padding: '4px 8px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            onClick={() => (window.location.href = '/verifications')}
          >
            Voir
          </button>
        )
      }
    );
  });

  // 🔍 Vérification analysée par IA
  socket.on('verification_analyzed', data => {
    const isAutoApprove = data.decision === 'auto_approve';
    const isAutoReject = data.decision === 'auto_reject';
    const isFlagged = data.decision === 'flag_review';

    let message = '';
    let variant = 'info';

    if (isAutoApprove) {
      message = `✅ Vérification auto-approuvée: ${data.userName}`;
      variant = 'success';
    } else if (isAutoReject) {
      message = `❌ Vérification auto-rejetée: ${data.userName} (${data.reason})`;
      variant = 'warning';
    } else if (isFlagged) {
      message = `🔍 Vérification à examiner: ${data.userName}`;
      variant = 'warning';
    }

    if (message) {
      enqueue(message, {
        variant,
        autoHideDuration: isFlagged ? null : 6000,
        action: () => (
          <button
            style={{
              color: 'white',
              fontWeight: 'bold',
              background: 'transparent',
              border: '1px solid white',
              padding: '4px 8px',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            onClick={() =>
              (window.location.href = `/verifications?id=${data.verificationId}`)
            }
          >
            Voir
          </button>
        )
      });
    }
  });
}
